/**
 * 文件处理相关工具函数
 */

/**
 * 处理文件名，添加翻译标识和类型后缀
 * @param fileName 原始文件名
 * @param type PDF文件类型
 * @returns 处理后的文件名
 */
export function processFileName(fileName: string, type: ServerDataTypes.PDF.PdfFileType): string {
  const lastIndexOfDot = fileName.lastIndexOf('.')
  let name = fileName.substring(0, lastIndexOfDot)
  if (!name) {
    name = fileName
  }
  const suffix = fileName.substring(lastIndexOfDot)
  let newFileName = ''

  if (type === 'pdf_dual') {
    newFileName = `${name}${name.includes('会译') ? '' : '-会译'}-左右整页对照${suffix}`
  } else if (type === 'pdf_mono') {
    newFileName = `${name}${name.includes('会译') ? '' : '-会译'}-仅译文${suffix}`
  } else if (type === 'pdf_dual_page') {
    newFileName = `${name}${name.includes('会译') ? '' : '-会译'}-上下整页对照${suffix}`
  }

  return newFileName
}

/**
 * 生成带会译标识的文件名
 * @param fileName 原始文件名
 * @param suffix 文件后缀
 * @returns 处理后的文件名
 */
export function generateFileName(fileName: string, suffix: 'pdf'): string {
  const nameArr = fileName.split('.')

  let name = `${fileName}-会译.${suffix}`
  if (nameArr.length > 1) {
    nameArr.pop()
    name = `${nameArr.join('.')}-会译.${suffix}`
  }

  return name
}

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @returns 格式化后的文件大小字符串
 */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(0)}MB`
  } else {
    return `${(bytes / 1024).toFixed(0)}KB`
  }
}

// 根据总页数预估总翻译时间。返回总分钟数
function estimateTranslate({
  totalPage,
  vipVersion,
  batchFileCount
}: {
  totalPage: number,
  vipVersion: ServerDataTypes.VersionType,
  batchFileCount: number
}) {
  // 平均基础速度 5 页/分钟
  let baseSpeed = 5
  // 并发数量
  let batchCount = 1

  if (vipVersion !== 'free') {
    if (vipVersion === 'ultra') {
      baseSpeed *= 1.25
      batchCount = 5
    } else {
      baseSpeed *= 1.1
      batchCount = 2
    }
  }
  if (batchFileCount === 1 || batchCount > batchFileCount) {
    batchCount = batchFileCount
  }
  const totalMinutes = totalPage / baseSpeed / batchCount
  return Math.ceil(totalMinutes)
}

/**
 * 根据页数计算预估翻译时间
 * @param totalPage 总页数
 * @returns 预估时间描述
 */
export function estimateTranslateTime(totalPage: number, vipVersion: ServerDataTypes.VersionType): string {
  const totalMinutes = estimateTranslate({ totalPage, vipVersion, batchFileCount: 1 })
  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    if (minutes === 0) {
      return `${hours} 小时`
    } else {
      return `${hours} 小时 ${minutes} 分钟`
    }
  } else {
    return `${totalMinutes} 分钟`
  }
}

/**
 * 计算批量文件的预估翻译时间
 * @param fileList 文件列表
 * @returns 预估时间描述
 */
export function estimateBatchTranslateTime(
  fileList: Array<{ totalPage: number }>,
  vipVersion: ServerDataTypes.VersionType
): string | undefined {
  if (!fileList || fileList.length === 0) {
    return undefined
  }
  const totalPage = fileList.reduce((prev, cur) => {
    return prev + cur.totalPage
  }, 0)
  const totalMinutes = estimateTranslate({ totalPage, vipVersion, batchFileCount: 1 })
  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    if (minutes === 0) {
      return `${hours} 小时`
    } else {
      return `${hours} 小时 ${minutes} 分钟`
    }
  } else {
    return `${totalMinutes} 分钟`
  }
}

